import React, { useCallback, useMemo } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import ClearIcon from '@mui/icons-material/Clear';
import { CharacterCounter } from 'modules/common/components/Counter/CharacterCounter';
import { useMessage } from 'modules/intl';
import { useTextAreaScrollStyles } from 'modules/notes/hooks';
import {
  useFormErrorDialogContext,
  NOTE_FIELDS,
  NOTE_MAX_CHAR_LIMIT
} from 'modules/notes/common';
import {
  useFetchSuggestions,
  FetchSuggestionsHandler
} from 'modules/notes/addNote/hooks';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2)
  },
  noteDetails: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 2.5, 1, 2.5)
  },
  characterCount: {
    margin: 0
  },
  note: {
    width: 'calc(100% - 16px)',
    margin: theme.spacing(0, 0, 1, 1),
    '& $clearButton': {
      opacity: 1
    },
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5)
  },
  inputAdornment: {
    height: '100%',
    margin: theme.spacing(0, 0.5)
  },
  clearButton: {
    padding: theme.spacing(0),
    color: theme.palette.text.primary
  },
  materialUIInput: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingRight: theme.spacing(0.5)
  }
}));

export type DescriptionChangeProps = {
  fetchSuggestions: FetchSuggestionsHandler;
  description: string | undefined;
};

type NoteDescriptionProps = {
  onDescriptionBlur: () => void;
  onDescriptionChange: (props: DescriptionChangeProps) => void;
};

const textfieldLabel = <FormattedMessage id="note.yourNote" />;
export const NoteDescription: React.FC<NoteDescriptionProps> = ({
  onDescriptionBlur,
  onDescriptionChange
}: NoteDescriptionProps) => {
  const classesScroll = useTextAreaScrollStyles();
  const placeHolder = useMessage('note.addNotePlaceholder');
  const { errorContext, setErrorContext } = useFormErrorDialogContext();
  const {
    control,
    setValue,
    register,
    clearErrors,
    formState: { errors }
  } = useFormContext();
  const fetchSuggestions = useFetchSuggestions();

  const inputProps = useMemo(
    () => ({
      'data-testid': 'add-note-comment-input'
    }),
    []
  );

  const { onBlur, onChange, ...rest } = register(NOTE_FIELDS.NOTE);

  const handleDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const description = event.target.value;
      setValue(NOTE_FIELDS.NOTE, description, {
        shouldDirty: true
      });
      onChange(event);
      const isError = Boolean((description?.length ?? 0) > NOTE_MAX_CHAR_LIMIT);
      setErrorContext((prev) => {
        if (prev.noteExceeded !== isError) {
          return { ...prev, noteExceeded: isError };
        }
        return prev;
      });
      onDescriptionChange({ fetchSuggestions, description });
    },
    [setValue, onChange, setErrorContext, onDescriptionChange, fetchSuggestions]
  );

  const onNoteDescriptionBlur = useCallback(
    (e) => {
      onBlur(e);
      onDescriptionBlur();
    },
    [onBlur, onDescriptionBlur]
  );
  const classes = useStyles();
  const characterCountClasses = useMemo(
    () => ({
      root: classes.characterCount
    }),
    [classes.characterCount]
  );

  const noteDescription = useWatch({ control, name: NOTE_FIELDS.NOTE });

  const clearNote = useCallback(() => {
    setValue(NOTE_FIELDS.NOTE, undefined, {
      shouldDirty: true
    });
    clearErrors(NOTE_FIELDS.NOTE);
    setErrorContext((prev) => {
      return { ...prev, ...{ noteExceeded: false } };
    });
  }, [setValue, setErrorContext, clearErrors]);

  const InputProps = useMemo(
    () => ({
      endAdornment: noteDescription ? (
        <InputAdornment className={classes.inputAdornment} position="end">
          <IconButton
            className={classes.clearButton}
            data-testid={'input-clear-button'}
            onClick={clearNote}
            size="medium"
          >
            <ClearIcon fontSize={'small'} />
          </IconButton>
        </InputAdornment>
      ) : (
        <></>
      ),
      className: classes.materialUIInput
    }),
    [
      classes.clearButton,
      classes.inputAdornment,
      classes.materialUIInput,
      clearNote,
      noteDescription
    ]
  );

  return (
    <div className={classes.container}>
      <TextField
        data-testid="add-note-comment"
        value={noteDescription}
        label={textfieldLabel}
        className={classNames(classes.note, classesScroll.scrollBar)}
        autoFocus
        multiline
        maxRows={2}
        inputProps={inputProps}
        {...rest}
        onChange={handleDescriptionChange}
        onBlur={onNoteDescriptionBlur}
        error={Boolean(errors[NOTE_FIELDS.NOTE]) || errorContext.noteExceeded}
        placeholder={placeHolder}
        variant="outlined"
        size="small"
        InputProps={InputProps}
      />
      <CharacterCounter
        text={noteDescription}
        hasError={
          Boolean(errors[NOTE_FIELDS.NOTE]) || errorContext.noteExceeded
        }
        classes={characterCountClasses}
        totalChar={NOTE_MAX_CHAR_LIMIT}
      />
    </div>
  );
};
